import { Show, createEffect, createSignal, onCleanup } from "solid-js";
import { scanQrFromCamera } from "../services/qrScanner";
import { styles } from "../styles";

export type QrRegistrationTarget = {
  title: string;
};

type Props = {
  target: QrRegistrationTarget | null;
  onClose: () => void;
  onRegister: (qr: string) => void;
};

export function QrRegistrationModal(props: Props) {
  const [state, setState] = createSignal<"reading" | "error">("reading");
  const [error, setError] = createSignal("");
  let video!: HTMLVideoElement;
  let controller: AbortController | undefined;

  const stopReading = () => {
    controller?.abort();
    controller = undefined;
  };
  const close = () => {
    stopReading();
    props.onClose();
  };
  const startReading = async () => {
    if (!props.target || controller) return;
    stopReading();
    controller = new AbortController();
    setError("");
    setState("reading");
    try {
      const qr = await scanQrFromCamera(video, controller.signal);
      props.onRegister(qr);
      close();
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError") return;
      setError(cause instanceof Error ? cause.message : String(cause));
      setState("error");
    } finally {
      controller = undefined;
    }
  };

  createEffect(() => {
    const target = props.target;
    stopReading();
    setState("reading");
    setError("");
    if (target) {
      queueMicrotask(() => {
        if (props.target === target) void startReading();
      });
    }
  });
  onCleanup(stopReading);

  return (
    <Show when={props.target}>
      {(target) => (
        <div class={styles.qrRegistrationLayer} role="presentation">
          <button class={styles.previewBackdrop} type="button" aria-label="QR登録を閉じる" onClick={close} />
          <section class={styles.qrRegistrationModal} role="dialog" aria-modal="true" aria-labelledby="qr-registration-title">
            <header class={styles.previewHeader}>
              <h2 id="qr-registration-title">QR登録：{target().title}</h2>
              <button class={styles.iconButton} type="button" aria-label="閉じる" onClick={close}>×</button>
            </header>
            <div class={styles.qrRegistrationBody}>
              <video
                ref={video}
                class={styles.qrCameraPreview}
                playsinline
                muted
                style={{ display: state() === "reading" ? "block" : "none" }}
              />
              <Show when={state() === "reading"}><p>QRをカメラ中央に映してください…</p></Show>
              <Show when={error()}><p class={styles.qrRegistrationError} role="alert">{error()}</p></Show>
              <div class={styles.qrRegistrationActions}>
                <button class={styles.secondaryButton} type="button" onClick={close}>閉じる</button>
              </div>
            </div>
          </section>
        </div>
      )}
    </Show>
  );
}
